/**
 * Policy Detail — Full policy view with tabs: Overview, Versions, Citations, Audit.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useAgentProgress from '../../lib/useAgentProgress';
import AgentThinkingPanel from '../../components/AgentThinkingPanel';
import CounselPanel from './CounselPanel';
import ConditionAnswer, { fetchConditionAnswers } from './ConditionAnswer';
import { TakeOnBlock } from './RuleTakeOn';

const API = process.env.REACT_APP_API_URL;

/**
 * Quiet check — the compliance-only preflight of the Policy Review Guide.
 * No campaign, no tasks, no emails; the result is visible right here and
 * nowhere else. Same agent, same verified-citation discipline as the live
 * annual review.
 */
function QuietCheck({ policyId }) {
  const [state, setState] = useState({ phase: 'idle' }); // idle | generating | ready | failed
  const [condAnswers, setCondAnswers] = useState(null);
  useEffect(() => { fetchConditionAnswers().then(setCondAnswers); }, []);
  const pollRef = React.useRef(null);

  const load = React.useCallback(async () => {
    const r = await fetch(`${API}/api/ppm/policies/${policyId}/preflight`, { credentials: 'include' });
    if (r.status === 404) { setState({ phase: 'idle' }); return 'idle'; }
    const d = await r.json().catch(() => null);
    if (r.status === 202) { setState({ phase: 'generating' }); return 'generating'; }
    if (d?.status === 'ready') { setState({ phase: 'ready', data: d }); return 'ready'; }
    if (d?.status === 'failed') { setState({ phase: 'failed', message: d.message }); return 'failed'; }
    setState({ phase: 'idle' }); return 'idle';
  }, [policyId]);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const startPolling = React.useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const phase = await load();
      if (phase !== 'generating') clearInterval(pollRef.current);
    }, 7000);
  }, [load]);

  const run = async () => {
    setState({ phase: 'generating' });
    await fetch(`${API}/api/ppm/policies/${policyId}/preflight`, { method: 'POST', credentials: 'include' });
    startPolling();
  };
  useEffect(() => { if (state.phase === 'generating') startPolling(); return () => clearInterval(pollRef.current); }, [state.phase, startPolling]);

  const g = state.data?.guidance || {};
  const sections = Array.isArray(g.sections) ? g.sections : [];
  const flags = sections.filter(s => s.status === 'flag');
  const unassessed = sections.filter(s => s.status === 'no_assessment');
  const clean = sections.filter(s => s.status === 'no_changes');

  return (
    <div className="mt-6 pt-5 border-t border-slate-200">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Quiet check</h3>
          <p className="text-xs text-slate-400 mt-0.5">Run the review agent on this policy privately. No tasks, no emails — the result appears here and nowhere else.</p>
        </div>
        <button type="button" onClick={run} disabled={state.phase === 'generating'}
          className="px-3.5 py-2 rounded-xl border border-orange-300 text-orange-700 text-[13px] font-medium hover:bg-orange-50 disabled:opacity-50">
          {state.phase === 'generating' ? 'Checking…' : state.phase === 'ready' ? 'Run it again' : 'Run a quiet check'}
        </button>
      </div>

      {state.phase === 'generating' && (
        <p className="text-[13px] text-slate-500 mt-3">Reading the policy against the regulatory corpus, final-rule analyses, memo guidance, and your state contract. This can take a few minutes.</p>
      )}
      {state.phase === 'failed' && (
        <p className="text-[13px] text-red-600 mt-3">{state.message || 'The check failed. Try again.'}</p>
      )}
      {state.phase === 'ready' && (
        <div className="mt-3 space-y-2.5">
          {g.overall_summary && <p className="text-[13px] text-slate-600 leading-relaxed">{g.overall_summary}</p>}
          {flags.map(s => (
            <div key={s.index} className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="text-xs font-semibold text-slate-700">{s.heading}</p>
              {s.applies === 'conditional' && s.condition && (
                <ConditionAnswer condition={s.condition} policyId={policyId} stored={condAnswers?.get(s.condition)} compact />
              )}
              <p className="text-xs text-slate-600 mt-1">{s.note}</p>
              {(s.citations || []).map((c, i) => (
                <div key={i} className="mt-1.5 border-l-2 border-slate-300 pl-2.5">
                  <p className="text-[11px] font-semibold text-slate-500">{c.source === 'state_contract' ? (c.label || 'State Medicaid contract') : c.source === 'requirement' ? (c.label || 'Requirement on file') : c.citation}</p>
                  <blockquote className="text-[11.5px] italic text-slate-500">&ldquo;{c.quote}&rdquo;</blockquote>
                </div>
              ))}
            </div>
          ))}
          {flags.length === 0 && <p className="text-[13px] text-slate-600">No sections flagged.</p>}
          <TakeOnBlock items={g.take_on} compact />
          {unassessed.length > 0 && <p className="text-[11.5px] text-amber-700">Not assessed (treat as unreviewed, not cleared): {unassessed.map(s => s.heading).join('; ')}.</p>}
          {clean.length > 0 && <p className="text-[11.5px] text-slate-500">Checked with no divergence: {clean.map(s => s.heading).join('; ')}.</p>}
          {(g.corpus_gaps || []).map((gap, i) => <p key={i} className="text-[11px] text-slate-400">{gap}</p>)}
          <p className="text-[11px] text-slate-400">
            {state.data.citations_verified || 0} verified citations · policy v{state.data.policy_version_number}
            {state.data.generated_at ? ` · ${new Date(state.data.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''} · visible to compliance only
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * The policy as it was uploaded — exact layout, not extracted text. PDFs
 * render inline through the file route's ?inline=1 contract; Word documents
 * (which browsers cannot render) get a one-click open of the original, with
 * the extracted text as the on-screen fallback.
 */
function OriginalDocument({ policyId, fallbackText }) {
  const [file, setFile] = useState(null);
  const [checked, setChecked] = useState(false);
  const [docxFailed, setDocxFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [expanded, setExpanded] = useState(false);
  const docxHost = useRef(null);
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);
  useEffect(() => {
    let alive = true;
    setFile(null); setChecked(false); setDocxFailed(false);
    fetch(`${API}/api/ppm/policies/${policyId}/files`, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!alive) return;
        const list = d?.files || d || [];
        setFile(Array.isArray(list) && list.length ? list[0] : null);
        setChecked(true);
      })
      .catch(() => { if (alive) setChecked(true); });
    return () => { alive = false; };
  }, [policyId]);

  const isPdf = file && /pdf/i.test(file.mime_type || file.file_name || '');
  const isDocx = file && !isPdf && /(\.docx$|officedocument\.wordprocessingml)/i.test(`${file.file_name || ''} ${file.mime_type || ''}`);

  // Word documents render in the panel itself: fetch the real .docx and draw
  // it client-side (docx-preview — no external service sees the document).
  useEffect(() => {
    if (!isDocx || docxFailed) return;
    let alive = true;
    Promise.all([
      import('docx-preview'),
      fetch(`${API}/api/ppm/policies/${policyId}/file`, { credentials: 'include' })
        .then(r => { if (!r.ok) throw new Error('file fetch failed'); return r.arrayBuffer(); }),
    ])
      .then(([{ renderAsync }, buf]) => {
        if (!alive || !docxHost.current) return;
        docxHost.current.innerHTML = '';
        // The panel shows the document the way Word lays it out: real pages,
        // real margins, headers and footers, the client's own look. Reflowing
        // it to the panel width made every policy look like somebody else's
        // document (operator report 2026-09-22). The zoom control is how a
        // narrow panel is handled.
        return renderAsync(buf, docxHost.current, undefined, {
          inWrapper: true, ignoreWidth: false, ignoreHeight: false, experimental: true,
          renderHeaders: true, renderFooters: true, breakPages: true,
        });
      })
      .catch(() => { if (alive) setDocxFailed(true); });
    return () => { alive = false; };
  }, [isDocx, docxFailed, policyId]);

  if (!checked) return <p className="text-sm text-slate-400">Loading document…</p>;

  // Shell shared by both renderers: a toolbar (zoom for Word, expand for
  // both) and an optional full-screen overlay of the same viewer.
  const frame = expanded
    ? 'fixed inset-4 z-50 bg-white rounded-2xl shadow-2xl border border-slate-300 flex flex-col p-4'
    : '';
  const viewerHeight = expanded ? 'flex-1 min-h-0' : 'h-[38rem]';
  const Toolbar = ({ withZoom }) => (
    <div className="flex items-center justify-end gap-1 mb-2">
      {withZoom && (
        <>
          <button type="button" onClick={() => setZoom(z => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))}
            className="px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 text-sm font-semibold hover:bg-slate-50" title="Zoom out">−</button>
          <span className="px-1.5 text-xs text-slate-500 tabular-nums w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button type="button" onClick={() => setZoom(z => Math.min(2.5, Math.round((z + 0.1) * 10) / 10))}
            className="px-2.5 py-1 rounded-lg border border-slate-300 text-slate-600 text-sm font-semibold hover:bg-slate-50" title="Zoom in">+</button>
        </>
      )}
      <button type="button" onClick={() => setExpanded(e => !e)}
        className="ml-1 px-3 py-1 rounded-lg border border-orange-300 text-orange-700 text-xs font-medium hover:bg-orange-50">
        {expanded ? 'Close' : 'Expand'}
      </button>
    </div>
  );

  if (isPdf) {
    return (
      <>
        {expanded && <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={() => setExpanded(false)} />}
        <div className={frame}>
          <Toolbar withZoom={false} />
          <iframe
            title="Original policy document"
            src={`${API}/api/ppm/policies/${policyId}/file?inline=1`}
            className={`w-full ${viewerHeight} rounded-xl border border-slate-200 bg-white`}
          />
          {!expanded && (
            <a href={`${API}/api/ppm/policies/${policyId}/file?inline=1`} target="_blank" rel="noreferrer"
              className="inline-block mt-2 text-xs font-medium text-orange-700 hover:text-orange-900">
              Open the original in its own tab
            </a>
          )}
        </div>
      </>
    );
  }
  if (isDocx && !docxFailed) {
    return (
      <>
        {expanded && <div className="fixed inset-0 z-40 bg-slate-900/40" onClick={() => setExpanded(false)} />}
        <div className={frame}>
          <Toolbar withZoom />
          <div className={`${viewerHeight} overflow-auto rounded-xl border border-slate-200 bg-slate-100`}>
            <div ref={docxHost} className="ppm-docx-host" style={{ zoom }}>
              <p className="text-sm text-slate-400 p-4">Loading the original document…</p>
            </div>
          </div>
          {!expanded && (
            <a href={`${API}/api/ppm/policies/${policyId}/file`}
              className="inline-block mt-2 text-xs font-medium text-orange-700 hover:text-orange-900">
              Download the original ({file.file_name})
            </a>
          )}
        </div>
      </>
    );
  }
  return (
    <div>
      {file && (
        <a href={`${API}/api/ppm/policies/${policyId}/file`}
          className="inline-block mb-2 px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 text-xs font-medium hover:bg-orange-50">
          Open the original document ({file.file_name})
        </a>
      )}
      <div className="max-h-[28rem] overflow-y-auto pr-1">
        <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{fallbackText}</p>
      </div>
    </div>
  );
}

const STATUS_CFG = {
  draft:        { label: 'Draft',       color: 'bg-slate-100 text-slate-600 border-slate-200' },
  active:       { label: 'Active',      color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  under_review: { label: 'Under Review',color: 'bg-amber-50 text-amber-700 border-amber-200' },
  superseded:   { label: 'Superseded',  color: 'bg-slate-50 text-slate-500 border-slate-200' },
  archived:     { label: 'Archived',    color: 'bg-slate-50 text-slate-400 border-slate-200' },
};

const TABS = ['overview', 'versions', 'citations', 'counsel', 'audit'];

// The visible lifecycle, in order, plus the transitions each status offers.
// Superseded is system-set (a new version closes the old one), so it renders
// in the strip but is never a button target.
const LIFECYCLE = ['draft', 'active', 'under_review', 'superseded', 'archived'];
const TRANSITIONS = {
  draft:        [{ to: 'active', label: 'Activate', tone: 'emerald', tip: 'Publish this policy as the in-force version.' }],
  active:       [{ to: 'under_review', label: 'Start review', tone: 'amber', tip: 'Flag this policy as under formal review.' },
                 { to: 'archived', label: 'Archive', tone: 'slate', confirm: true, tip: 'Retire this policy. It stays in the audit trail.' }],
  under_review: [{ to: 'active', label: 'Complete review', tone: 'emerald', tip: 'Return this policy to active after review.' },
                 { to: 'archived', label: 'Archive', tone: 'slate', confirm: true, tip: 'Retire this policy. It stays in the audit trail.' }],
  superseded:   [{ to: 'archived', label: 'Archive', tone: 'slate', confirm: true, tip: 'Retire this superseded policy.' }],
  archived:     [{ to: 'draft', label: 'Restore as draft', tone: 'slate', tip: 'Bring this policy back as a draft.' }],
};

function LifecycleStrip({ policy, onChanged }) {
  const [busy, setBusy] = useState(false);
  const actions = TRANSITIONS[policy.status] || [];

  const transition = async (t) => {
    if (t.confirm && !window.confirm(`${t.label} "${policy.title}"? This is recorded in the audit trail.`)) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/ppm/policies/${policy.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: t.to }),
      });
      const data = await res.json();
      if (data.success) onChanged();
    } catch {}
    setBusy(false);
  };

  const toneBtn = {
    emerald: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    amber: 'bg-amber-600 hover:bg-amber-700 text-white',
    slate: 'border border-slate-200 text-slate-600 hover:bg-slate-50',
  };

  return (
    <div className="mb-6 bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between gap-4 flex-wrap">
      <div className="flex items-center gap-1.5 flex-wrap">
        {LIFECYCLE.map((s, i) => {
          const cfg = STATUS_CFG[s];
          const isCurrent = policy.status === s;
          return (
            <React.Fragment key={s}>
              {i > 0 && <span className="text-slate-300 text-xs">→</span>}
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${
                isCurrent ? 'bg-orange-600 text-white border-orange-600 shadow-sm' : cfg.color
              }`}>{cfg.label}</span>
            </React.Fragment>
          );
        })}
      </div>
      <div className="flex items-center gap-2">
        {actions.map(t => (
          <button key={t.to} onClick={() => transition(t)} disabled={busy} title={t.tip}
            className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-colors disabled:opacity-50 ${toneBtn[t.tone]}`}>
            {t.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PolicyDetail({ policyId, onBack }) {
  const [policy, setPolicy] = useState(null);
  const [versions, setVersions] = useState([]);
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [auditEvents, setAuditEvents] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/ppm/policies/${policyId}`, { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setPolicy(data.policy);
        setVersions(data.versions || []);
        setCitations(data.citations || []);
      }
    } catch {}
    setLoading(false);
  }, [policyId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab !== 'audit') return;
    (async () => {
      try {
        const res = await fetch(`${API}/api/ppm/audit`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setAuditEvents((data.events || []).filter(e => e.entity_id === policyId));
        }
      } catch {}
    })();
  }, [tab, policyId]);

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading policy...</div>;
  if (!policy) return <div className="p-8 text-red-500 text-sm">Policy not found. <button onClick={onBack} className="underline ml-1">Back</button></div>;

  const status = STATUS_CFG[policy.status] || STATUS_CFG.draft;
  const currentVersion = versions.find(v => !v.effective_to) || versions[0];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Back + Title */}
      <div className="mb-6">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-700 font-medium mb-3 transition-colors">← Back to library</button>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5 mb-1 flex-wrap">
              <span className="text-xs font-mono text-slate-400">{policy.policy_number}</span>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${status.color}`}>{status.label}</span>
              {policy.category && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-50 text-slate-500 border border-slate-200 uppercase tracking-wide">{policy.category}</span>
              )}
            </div>
            <h1 className="text-xl font-bold text-slate-800 tracking-tight">{policy.title}</h1>
            {policy.description && <p className="text-sm text-slate-500 mt-1 max-w-2xl">{policy.description}</p>}
          </div>
          <div className="text-right text-xs text-slate-400 flex-shrink-0">
            {policy.owner_name && <div>Owner: <span className="text-slate-600 font-medium">{policy.owner_name}</span></div>}
            {policy.department_name && <div>Dept: <span className="text-slate-600">{policy.department_name}</span></div>}
            <div>Review: every {policy.review_cycle_months}mo</div>
            {currentVersion && <div className="mt-1 text-orange-600 font-medium">Current: v{currentVersion.version_number}</div>}
          </div>
        </div>
      </div>

      {/* Agent activity — surfaces the in-flight policy intake agents
          (citation classifier, owner inference) as well as the POM
          contradiction detector that runs over this document's text.
          Both render nothing until the corresponding events arrive. */}
      <PolicyAgentActivity policyId={policyId} />
      <PolicyPomDocumentActivity documentId={policyId} />

      {/* Lifecycle — where this policy sits in draft → active → review →
          superseded → archived, with the permitted transitions as actions. */}
      <LifecycleStrip policy={policy} onChanged={load} />

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 bg-slate-100/80 rounded-2xl w-fit flex-wrap">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              tab === t ? 'bg-gradient-to-r from-orange-600 to-amber-500 text-white shadow-lg shadow-orange-600/20' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {t.charAt(0).toUpperCase() + t.slice(1)}
            {t === 'versions' && versions.length > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-bold ${tab === t ? 'bg-white/20' : 'bg-slate-200 text-slate-500'}`}>{versions.length}</span>
            )}
            {t === 'citations' && citations.length > 0 && (
              <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full font-bold ${tab === t ? 'bg-white/20' : 'bg-slate-200 text-slate-500'}`}>{citations.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6">
          {/* The document is the hero: it takes the full width it needs to be
              read. Policy Information moves to a slim right rail that sticks in
              view while you scroll the policy, so it never squeezes the text. */}
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr),300px] gap-6 items-start">
            {currentVersion && (
              <div className="min-w-0 lg:order-1">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Current Version (v{currentVersion.version_number})</h3>
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-4">
                  {currentVersion.change_summary && (
                    <p className="text-xs text-slate-500 mb-2 italic">{currentVersion.change_summary}</p>
                  )}
                  <OriginalDocument policyId={policy.id} fallbackText={currentVersion.content} />
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-400">
                    <span>Effective: {currentVersion.effective_from}</span>
                    {currentVersion.approved_by_name && <span>Approved by: {currentVersion.approved_by_name}</span>}
                  </div>
                </div>
              </div>
            )}
            <div className={`lg:order-2 lg:sticky lg:top-4 ${currentVersion ? 'lg:border-l lg:border-slate-100 lg:pl-6' : ''}`}>
              <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Policy Information</h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Number</dt><dd className="font-mono text-slate-700 text-right">{policy.policy_number}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Category</dt><dd className="text-slate-700 capitalize text-right">{policy.category || '—'}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Status</dt><dd><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${status.color}`}>{status.label}</span></dd></div>
                <OwnerRow policy={policy} onChanged={load} />
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Department</dt><dd className="text-slate-700 text-right">{policy.department_name || 'Org-wide'}</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Review Cycle</dt><dd className="text-slate-700 text-right">{policy.review_cycle_months} months</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Next Review</dt><dd className="text-slate-700 text-right">{policy.next_review_date || 'Not set'}</dd></div>
                <ExpirationRow policy={policy} onChanged={load} />
                <div className="flex justify-between gap-3"><dt className="text-slate-400">Created</dt><dd className="text-slate-700 text-right">{new Date(policy.created_at).toLocaleDateString()}</dd></div>
              </dl>
            </div>
          </div>
          <QuietCheck policyId={policy.id} />
        </div>
      )}

      {/* Versions */}
      {tab === 'versions' && (
        <div className="space-y-3">
          {versions.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
              No versions yet. Create the first version to establish this policy.
            </div>
          )}
          {versions.map(v => (
            <div key={v.id} className={`bg-white rounded-2xl border p-5 ${!v.effective_to ? 'border-orange-200 bg-orange-50/30' : 'border-slate-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800">Version {v.version_number}</span>
                  {!v.effective_to && <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-orange-100 text-orange-700 border border-orange-200">Current</span>}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">{v.effective_from}{v.effective_to ? ` → ${v.effective_to}` : ' → present'}</span>
                  {/* View — PDFs render in the browser tab; DOCX/DOC are
                      converted to styled HTML server-side via mammoth.
                      Unknown mime types are download-only. */}
                  {(v.file_mime === 'application/pdf'
                    || v.file_mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    || v.file_mime === 'application/msword') && (
                    <a href={`${API}/api/ppm/policies/${policyId}/versions/${v.id}/file?inline=1`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={v.file_name ? `View ${v.file_name}` : 'View original'}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      View
                    </a>
                  )}
                  <a href={`${API}/api/ppm/policies/${policyId}/versions/${v.id}/file`}
                    title={v.file_name ? `Download ${v.file_name}` : 'Download original'}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-xs font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download original
                  </a>
                </div>
              </div>
              {v.change_summary && <p className="text-xs text-slate-500 mb-2 italic">{v.change_summary}</p>}
              <p className="text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{v.content?.slice(0, 500)}{v.content?.length > 500 ? '...' : ''}</p>
              <div className="mt-2 text-xs text-slate-400">
                {v.approved_by_name && <span>Approved by {v.approved_by_name} on {new Date(v.approved_at).toLocaleDateString()}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Citations */}
      {tab === 'citations' && (
        <div className="space-y-3">
          {citations.length === 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center text-slate-400 text-sm">
              No regulatory citations linked. Link CFR sections to establish traceability.
            </div>
          )}
          {citations.map(c => (
            <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-3 mb-2">
                <span className="px-2.5 py-1 rounded-lg bg-orange-50 text-orange-700 text-xs font-bold border border-orange-200">{c.cfr_section}</span>
                <span className="text-sm font-medium text-slate-700">{c.mandate_title}</span>
              </div>
              {c.citation_text && <p className="text-xs text-slate-500 italic">"{c.citation_text}"</p>}
              {c.relevance_notes && <p className="text-xs text-slate-400 mt-1">{c.relevance_notes}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Counsel */}
      {tab === 'counsel' && (
        <CounselPanel
          subjectType="policy"
          subjectId={policyId}
          defaultQuestion={`Does policy ${policy.policy_number} (“${policy.title}”) satisfy its cited CFR requirements, and what changes if any does the regulation require?`}
        />
      )}

      {/* Audit */}
      {tab === 'audit' && (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          {auditEvents.length === 0 && (
            <div className="p-8 text-center text-slate-400 text-sm">No audit events for this policy.</div>
          )}
          {auditEvents.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide">Event</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide">Actor</th>
                  <th className="text-left py-3 px-5 font-semibold text-xs text-slate-500 uppercase tracking-wide">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {auditEvents.map(e => (
                  <tr key={e.id} className="hover:bg-slate-50/50">
                    <td className="py-3 px-5 text-slate-700 font-medium">{e.event_type.replace(/_/g, ' ')}</td>
                    <td className="py-3 px-5 text-slate-500">{e.actor_name || '—'}</td>
                    <td className="py-3 px-5 text-slate-400">{new Date(e.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// OwnerRow — the Owner line with an inline Change control (same pattern as
// ExpirationRow). The SERVER decides who may reassign: compliance-level only
// (PATCH refuses anyone else with a plain message, which is surfaced here).
// Candidates come from /upload-meta's active-user list, fetched on demand.
// ---------------------------------------------------------------------------
function OwnerRow({ policy, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [users, setUsers] = useState(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const startEdit = async () => {
    setErr(null);
    setEditing(true);
    setValue(policy.owner_user_id || '');
    if (!users) {
      try {
        const res = await fetch(`${API}/api/ppm/upload-meta`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) setUsers((data.users || []).slice().sort((a, b) => String(a.name || a.email).localeCompare(String(b.name || b.email))));
        else setUsers([]);
      } catch { setUsers([]); }
    }
  };

  const save = async () => {
    if (!value || value === policy.owner_user_id) { setEditing(false); return; }
    setBusy(true); setErr(null);
    try {
      const res = await fetch(`${API}/api/ppm/policies/${policy.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ owner_user_id: value }),
      });
      const data = await res.json();
      if (data.success) { setEditing(false); onChanged(); }
      else setErr(data.message || 'Could not change the owner.');
    } catch { setErr('Could not change the owner.'); }
    setBusy(false);
  };

  if (editing) {
    return (
      <div className="space-y-1">
        <div className="flex justify-between items-center gap-2">
          <dt className="text-slate-400">Owner</dt>
          <dd className="flex items-center gap-1.5">
            <select value={value} onChange={(e) => setValue(e.target.value)} disabled={busy || users === null}
              className="text-xs border border-slate-300 rounded px-1.5 py-1 max-w-[180px]">
              <option value="">{users === null ? 'Loading…' : 'Select owner…'}</option>
              {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </select>
            <button type="button" onClick={save} disabled={busy || !value}
              className="text-xs px-2 py-1 rounded bg-slate-800 text-white disabled:opacity-40">{busy ? '…' : 'Save'}</button>
            <button type="button" onClick={() => { setEditing(false); setErr(null); }} disabled={busy}
              className="text-xs px-1.5 py-1 rounded border border-slate-300 text-slate-600">Cancel</button>
          </dd>
        </div>
        {err && <div className="text-[11px] text-red-600 text-right">{err}</div>}
      </div>
    );
  }
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-400">Owner</dt>
      <dd className="text-slate-700 text-right">
        {policy.owner_name || '—'}
        <button type="button" onClick={startEdit} className="ml-1.5 text-xs text-orange-600 hover:text-orange-700 font-medium">Change</button>
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ExpirationRow — shows the policy's expiration date in the Overview panel
// and lets compliance correct or clear it. This single field drives the
// library's red "Expired" badge, and the upload extractor can misread a
// review/renewal date printed in the document as an expiration — so the
// date has to be both visible and fixable here. Changes go through the
// standard PATCH route and land in the policy audit trail.
// ---------------------------------------------------------------------------
function ExpirationRow({ policy, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);

  const current = policy.expiration_date ? String(policy.expiration_date).slice(0, 10) : null;
  const isPast = current && current < new Date().toISOString().slice(0, 10);

  const save = async (next) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/ppm/policies/${policy.id}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ expiration_date: next }),
      });
      const data = await res.json();
      if (data.success) { setEditing(false); onChanged(); }
    } catch {}
    setBusy(false);
  };

  if (editing) {
    return (
      <div className="flex justify-between items-center gap-2">
        <dt className="text-slate-400">Expiration</dt>
        <dd className="flex items-center gap-1.5">
          <input type="date" value={value} onChange={e => setValue(e.target.value)}
            className="rounded-lg border border-slate-200 px-2 py-1 text-xs text-slate-700 focus:ring-2 focus:ring-orange-500 focus:border-orange-500" />
          <button onClick={() => value && save(value)} disabled={busy || !value}
            className="px-2 py-1 rounded-lg text-xs font-semibold bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">Save</button>
          <button onClick={() => save(null)} disabled={busy} title="Remove the expiration date entirely"
            className="px-2 py-1 rounded-lg text-xs font-semibold border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50">Clear</button>
          <button onClick={() => setEditing(false)} disabled={busy}
            className="px-2 py-1 rounded-lg text-xs text-slate-400 hover:text-slate-600">Cancel</button>
        </dd>
      </div>
    );
  }

  return (
    <div className="flex justify-between items-center">
      <dt className="text-slate-400">Expiration</dt>
      <dd className="flex items-center gap-2 text-slate-700">
        {current || 'None'}
        {isPast && (
          <span className="px-1.5 py-0.5 rounded-md text-[9px] font-semibold bg-red-100 text-red-800 border border-red-300">Expired</span>
        )}
        <button onClick={() => { setValue(current || ''); setEditing(true); }}
          className="text-xs font-medium text-orange-600 hover:text-orange-700">Change</button>
      </dd>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PolicyAgentActivity — polls /api/agent-progress for the policy intake
// agent (mandate classifier, owner inference) on this policy. Mirrors the
// OracleHome DocumentAgentActivity pattern.
// ---------------------------------------------------------------------------
function PolicyAgentActivity({ policyId }) {
  const { events, status } = useAgentProgress('policy', policyId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}

// ---------------------------------------------------------------------------
// PolicyPomDocumentActivity — surfaces the POM contradiction detector's
// in-flight events for the underlying document. The contradiction agent
// emits events under target_type='pom_document' during intake; the same
// document id is reused as the policy id.
// ---------------------------------------------------------------------------
function PolicyPomDocumentActivity({ documentId }) {
  const { events, status } = useAgentProgress('pom_document', documentId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Contradiction check" />;
}
